/**
 * PATH: packages/bills/src/components/PaymentSchedule.tsx
 * FILE_ID: BILLS-PKG-CMP-SCHEDULE-001
 * PURPOSE: Cash-out / Accounts Payable schedule view.
 *
 * WHY: Bookkeepers and finance leads need a single screen that
 *      answers "what bills are due / overdue right now and how much
 *      cash do I need to clear them?" — the AR/AP page in the CFO
 *      Hub answers that for the org-wide, Xero-synced view; this
 *      component does the same for the AI-extracted bills the
 *      Billing Hub controls (which may not be in Xero yet).
 *
 *      Inspired by `apps/cfo/src/pages/ARAPPage.tsx`'s structure:
 *      KPI strip → aging buckets → list. We deliberately stop short
 *      of a full calendar widget — the bucketed list (Overdue / This
 *      week / This month / Later) is what bookkeepers actually use.
 *
 * FLOW:
 *   ┌──────────────┐  group by  ┌────────────────┐  user clicks  ┌──────────┐
 *   │ /api/bills/  │ ─aging──▶  │ Bucket sections │ ─── row ───▶ │ workspace│
 *   │ (status=all) │            │ + KPI strip    │              │ ?id=...  │
 *   └──────────────┘            └────────────────┘              └──────────┘
 *
 * DEPENDENCIES:
 *   - useBillsList (../hooks) — same hook the workspace uses, so
 *     refresh + auth flow is identical.
 *   - BillListRow type from ../types.
 */

import { useMemo } from 'react';
import { useBillsList } from '../hooks';
import type { BillListRow } from '../types';
import {
  AGING_BUCKET_META,
  agingBucketKey,
  dueDatePill,
  isPayable,
  parseAmount,
  type AgingBucketKey,
} from '../billsAging';

interface Props {
  /**
   * Called when the user clicks a bill row. Apps wire this to
   * navigation (e.g. push to /bills/workspace?id=<billId>).
   */
  onSelectBill?: (billId: string) => void;
  /**
   * Polling interval. Defaults to 60s — slower than the workspace
   * because schedule data only really changes when a bill is
   * approved/pushed/extracted, not per-keystroke.
   */
  refetchIntervalMs?: number;
}

interface Bucket {
  key: AgingBucketKey;
  label: string;
  rangeLabel: string;
  tone: 'rose' | 'amber' | 'sky' | 'slate' | 'muted';
  rows: BillListRow[];
  total: number;
}

const TONE_BG: Record<Bucket['tone'], string> = {
  rose: 'bg-rose-50 ring-rose-200 dark:bg-rose-950/40',
  amber: 'bg-amber-50 ring-amber-200 dark:bg-amber-950/40',
  sky: 'bg-sky-50 ring-sky-200 dark:bg-sky-950/40',
  slate: 'bg-slate-50 ring-slate-200 dark:bg-slate-900/40',
  muted: 'bg-muted ring-border',
};

const TONE_TEXT: Record<Bucket['tone'], string> = {
  rose: 'text-rose-700 dark:text-rose-300',
  amber: 'text-amber-700 dark:text-amber-300',
  sky: 'text-sky-700 dark:text-sky-300',
  slate: 'text-slate-700 dark:text-slate-300',
  muted: 'text-muted-foreground',
};

export function PaymentSchedule({
  onSelectBill,
  refetchIntervalMs = 60_000,
}: Props) {
  const list = useBillsList({ status: 'all' }, refetchIntervalMs);

  const { buckets, kpis, currency } = useMemo(
    () => bucketise(list.data?.bills ?? []),
    [list.data],
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      <header className="shrink-0 border-b border-border px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold tracking-tight">Cash-out schedule</h2>
            <p className="text-xs text-muted-foreground">
              Unpaid supplier bills, grouped by when they're due. Click a row to open in the workspace.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void list.refetch()}
            className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            disabled={list.loading}
          >
            {list.loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <KpiRow kpis={kpis} currency={currency} loading={list.loading && !list.data} />

        {list.error ? (
          <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            {list.error}
          </div>
        ) : list.loading && !list.data ? (
          <p className="mt-6 text-sm text-muted-foreground">Loading schedule…</p>
        ) : buckets.every((b) => b.rows.length === 0) ? (
          <div className="mt-6 rounded-md border border-border bg-card p-6 text-center text-sm text-muted-foreground">
            No unpaid bills. You're all caught up.
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {buckets.map((b) =>
              b.rows.length === 0 ? null : (
                <BucketSection
                  key={b.key}
                  bucket={b}
                  currency={currency}
                  onSelectBill={onSelectBill}
                />
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────────── KPI strip ───────────────────── */

interface Kpis {
  overdue: number;
  thisWeek: number;
  thisMonth: number;
  outstanding: number;
  overdueCount: number;
  outstandingCount: number;
}

function KpiRow({
  kpis,
  currency,
  loading,
}: {
  kpis: Kpis;
  currency: string;
  loading: boolean;
}) {
  const cards = [
    {
      label: 'Overdue',
      value: kpis.overdue,
      sub: `${kpis.overdueCount} bill${kpis.overdueCount === 1 ? '' : 's'}`,
      tone: 'rose' as const,
    },
    {
      label: 'Due this week',
      value: kpis.thisWeek,
      sub: 'Next 7 days',
      tone: 'amber' as const,
    },
    {
      label: 'Due this month',
      value: kpis.thisMonth,
      sub: 'Next 30 days',
      tone: 'sky' as const,
    },
    {
      label: 'Outstanding',
      value: kpis.outstanding,
      sub: `${kpis.outstandingCount} bill${kpis.outstandingCount === 1 ? '' : 's'}`,
      tone: 'slate' as const,
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => (
        <div
          key={c.label}
          className={'rounded-xl border border-border bg-card p-4 ring-1 ' + TONE_BG[c.tone]}
        >
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {c.label}
          </p>
          <p className={'mt-1 text-2xl font-bold tabular-nums ' + TONE_TEXT[c.tone]}>
            {loading ? '—' : formatMoney(c.value, currency)}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}

/* ───────────────────── Bucket section ───────────────────── */

function BucketSection({
  bucket,
  currency,
  onSelectBill,
}: {
  bucket: Bucket;
  currency: string;
  onSelectBill?: (id: string) => void;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card">
      <header
        className={
          'flex items-center justify-between gap-3 px-4 py-2.5 ' +
          (bucket.tone === 'rose'
            ? 'bg-rose-50 dark:bg-rose-950/30'
            : bucket.tone === 'amber'
              ? 'bg-amber-50 dark:bg-amber-950/30'
              : bucket.tone === 'sky'
                ? 'bg-sky-50 dark:bg-sky-950/30'
                : 'bg-muted/30')
        }
      >
        <div className="flex items-baseline gap-2">
          <h3 className={'text-sm font-semibold ' + TONE_TEXT[bucket.tone]}>{bucket.label}</h3>
          <span className="text-xs text-muted-foreground">· {bucket.rangeLabel}</span>
          <span className="rounded-full bg-background px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-foreground/70">
            {bucket.rows.length}
          </span>
        </div>
        <p className={'text-sm font-semibold tabular-nums ' + TONE_TEXT[bucket.tone]}>
          {formatMoney(bucket.total, currency)}
        </p>
      </header>
      <ul className="divide-y divide-border">
        {bucket.rows.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => onSelectBill?.(row.id)}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-accent/40"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {row.vendor_name || '— Unknown vendor —'}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {row.invoice_number || row.email_subject || 'No reference'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <DueDatePill iso={row.due_date} />
                <span className="w-24 text-right text-sm font-medium tabular-nums">
                  {formatMoneyString(row.total, row.currency)}
                </span>
                <StatusPill status={row.status} />
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

const DUE_PILL_CLS: Record<ReturnType<typeof dueDatePill>['tone'], string> = {
  rose: 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300',
  amber: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  sky: 'border-sky-200 bg-sky-50/60 text-sky-700 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-300',
  slate: 'border-border bg-background text-muted-foreground',
  muted: 'border-border bg-background text-muted-foreground',
};

function DueDatePill({ iso }: { iso: string | null }) {
  const pill = dueDatePill(iso);
  return (
    <span
      className={
        'rounded-md border px-2 py-0.5 text-[10px] font-medium tabular-nums ' +
        DUE_PILL_CLS[pill.tone]
      }
    >
      {pill.label}
    </span>
  );
}

function StatusPill({ status }: { status: BillListRow['status'] }) {
  const map: Record<BillListRow['status'], string> = {
    pending: 'bg-amber-100 text-amber-800',
    extracted: 'bg-blue-100 text-blue-800',
    reviewed: 'bg-violet-100 text-violet-800',
    pushed: 'bg-emerald-100 text-emerald-800',
    failed: 'bg-rose-100 text-rose-800',
  };
  return (
    <span className={'rounded-full px-2 py-0.5 text-[10px] font-medium ' + map[status]}>
      {status}
    </span>
  );
}

/* ───────────────────── pure helpers (I→O) ───────────────────── */

/**
 * bucketise(rows) → { buckets, kpis, currency }.
 *
 * Excludes 'pushed' (= cleared in Xero) and 'failed' from the
 * outstanding total — the schedule is meant to drive future cash
 * flow, not be an audit log. 'pending' bills (no AI extraction yet)
 * are included if they have a due_date (rare) but otherwise land in
 * the no_due_date bucket so they're visible.
 */
export function bucketise(
  rows: BillListRow[],
  now: Date = new Date(),
): {
  buckets: Bucket[];
  kpis: Kpis;
  currency: string;
} {
  const init = (key: AgingBucketKey): Bucket => ({
    ...AGING_BUCKET_META[key],
    rows: [],
    total: 0,
  });
  const buckets: Record<AgingBucketKey, Bucket> = {
    overdue: init('overdue'),
    today: init('today'),
    this_week: init('this_week'),
    this_month: init('this_month'),
    later: init('later'),
    no_due_date: init('no_due_date'),
  };

  let outstandingCount = 0;
  let overdueCount = 0;
  let outstandingSum = 0;
  let detectedCurrency = 'AUD';

  for (const row of rows) {
    // Shared payable rule: drop paid + rejected; pushed-but-unpaid stays
    // (it's still cash owed). Unifies with billsAging.isPayable.
    if (!isPayable(row)) continue;
    outstandingCount++;
    const amount = parseAmount(row.total);
    outstandingSum += amount;
    if (row.currency) detectedCurrency = row.currency;

    const key = agingBucketKey(row.due_date, now);
    buckets[key].rows.push(row);
    buckets[key].total += amount;
    if (key === 'overdue') overdueCount++;
  }

  const sortByDue = (a: BillListRow, b: BillListRow) => {
    const ax = a.due_date ? new Date(a.due_date).getTime() : Infinity;
    const bx = b.due_date ? new Date(b.due_date).getTime() : Infinity;
    return ax - bx;
  };
  for (const k of Object.keys(buckets) as AgingBucketKey[]) {
    buckets[k].rows.sort(sortByDue);
  }

  const ordered: Bucket[] = [
    buckets.overdue,
    buckets.today,
    buckets.this_week,
    buckets.this_month,
    buckets.later,
    buckets.no_due_date,
  ];

  // KPI rollups derived from the (already-bucketed) totals so the strip
  // can never disagree with the sections below it.
  const weekSum = buckets.today.total + buckets.this_week.total;
  const monthSum = weekSum + buckets.this_month.total;

  return {
    buckets: ordered,
    kpis: {
      overdue: buckets.overdue.total,
      thisWeek: weekSum,
      thisMonth: monthSum,
      outstanding: outstandingSum,
      overdueCount,
      outstandingCount,
    },
    currency: detectedCurrency,
  };
}

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'AUD',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency || ''} ${amount.toFixed(0)}`;
  }
}

function formatMoneyString(
  amount: string | number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount == null || amount === '') return '—';
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(n)) return String(amount);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'AUD',
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${currency || ''} ${n.toFixed(2)}`;
  }
}
